import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_ROUTE } from "@/config/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ship, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e, em, pw) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const me = await login(em ?? email, pw ?? password);
      navigate(me.isAdmin ? DEFAULT_ROUTE.sales : DEFAULT_ROUTE[me.realRole] || DEFAULT_ROUTE.sales);
    } catch (err) {
      toast.error(!err.response
        ? "Cannot reach the FreightOps server. Start the backend on port 8000 and try again."
        : err.response?.data?.detail || "Invalid email or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center"><Ship className="h-5 w-5" /></div>
          <div><div className="font-display font-bold text-lg">JVK</div><div className="text-xs text-slate-400 -mt-0.5">Freight Operating System</div></div>
        </div>
        <div>
          <h1 className="font-display text-4xl font-bold leading-tight">Lead-to-cash control<br />for every shipment.</h1>
          <p className="text-slate-300 mt-4 max-w-md leading-6">A single operating workspace for Sales, Operations, Finance and Management—from first contact through job closure and audit.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-300 bg-white/5 border border-white/10 rounded-md px-3 py-2"><ShieldCheck className="h-3.5 w-3.5 text-blue-300" />Role-based access · auditable workflow</div>
        </div>
        <div className="text-xs text-slate-500">© 2026 JVK Shipping Services LLC · Dubai, UAE</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-6"><div className="h-9 w-9 rounded-lg bg-indigo-600 flex items-center justify-center"><Ship className="h-4 w-4 text-white" /></div><span className="font-display font-bold">JVK Freight OS</span></div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Use your work email to access your workspace.</p>

          <form onSubmit={doLogin} className="mt-6 space-y-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Email</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@jvk.example" className="mt-1" data-testid="login-email" required /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Password</label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" data-testid="login-password" required /></div>
            <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="login-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
          </form>

          <div className="mt-6 border-t border-slate-200 pt-4 text-xs leading-5 text-slate-500">Access is limited to authorized JVK personnel. Activity is recorded in the audit trail.</div>
        </div>
      </div>
    </div>
  );
}
